import { parseElementName } from '@adflow/types';

const SENSITIVE_KEYS = new Set([
  'importSourceJSON',
  'credentials',
  'userId',
  'token',
  'password',
  'authorization',
  'cookie',
  'svg',
  'content',
  'contentTemplate',
  'elements',
  'src',
  'url',
  'thumbnailURL',
  'thumbnail',
  'image',
  'png',
  'base64',
]);

const CONTENT_TOKEN_PATTERN_SOURCE = '\\{(?:[^:]+:)?([A-Z_][A-Z0-9_]*)\\}';

const URL_TEMPLATE_ELEMENT_TYPES = new Set(['IMAGE', 'TEAM_ASSET', 'CASINO_ASSET']);

export function hasDynamicContentTokens(content: string): boolean {
  return new RegExp(CONTENT_TOKEN_PATTERN_SOURCE, 'i').test(content);
}

/** Rejects partial image content that would break preview (token-only patches). */
export function validateImageContentWrite(content: unknown): string | null {
  if (content === '' || content === null) {
    return null;
  }
  if (typeof content !== 'string') {
    return null;
  }
  if (content === 'logo' || content === 'brand_logo') {
    return null;
  }
  if (looksLikeUrl(content)) {
    return null;
  }
  return 'Image content must be the full URL template from read contentTemplate, including the https URL and brace tokens.';
}

const TEXT_ALIGN = new Set(['left', 'center', 'right']);
const TEXT_ALIGN_VERTICAL = new Set(['top', 'center', 'bottom']);
const TEXT_CASE = new Set(['none', 'uppercase', 'lowercase', 'capitalize']);

export type TemplateBuilderReadSource = {
  sourceId: string;
  name: string;
};

export type TemplateBuilderReadCustomAsset = {
  folder: string;
  fileTemplate: string;
};

export type TemplateBuilderReadNameConfig = {
  isEditable: boolean;
  customAsset: TemplateBuilderReadCustomAsset | null;
};

export type TemplateBuilderReadDynamicFont =
  | { enabled: false }
  | {
      enabled: true;
      maxFontSize: number;
      minFontSize: number;
      lineHeight: number;
      multiLine: boolean;
    };

export type TemplateBuilderReadColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type TemplateBuilderReadFill = {
  type: string;
  color: TemplateBuilderReadColor | null;
  opacity: number | null;
  gradientStops: Array<{ position: number; color: TemplateBuilderReadColor }> | null;
  gradientHandlePositions: Array<{ x: number; y: number }> | null;
};

export type TemplateBuilderReadStyle = {
  textAlign: string | null;
  textAlignVertical: string | null;
  fontSize: number | null;
  lineHeight: number | null;
  letterSpacing: number | null;
  fontFamily: string | null;
  fontVariant: string | null;
  fontWeight: number | null;
  fontStyle: string | null;
  fontStretch: string | null;
  textCase: string | null;
  color: string | null;
  stroke: string | null;
  strokeWeight: number | null;
  fills: TemplateBuilderReadFill[] | null;
};

export type TemplateBuilderReadSize = {
  width: number;
  height: number;
};

export type TemplateBuilderReadElement = {
  id: string;
  type: string;
  name: string;
  position: { x: number; y: number; z: number };
  size: TemplateBuilderReadSize;
  rotation: number | null;
  hidden: boolean;
  locked: boolean;
  sources: TemplateBuilderReadSource[];
  nameConfig: TemplateBuilderReadNameConfig | null;
  contentTokens: string[];
  contentTemplate: string | null;
  dynamicFont: TemplateBuilderReadDynamicFont | null;
  style: TemplateBuilderReadStyle | null;
};

export type TemplateBuilderReadDataSource = {
  id: string;
  type: string;
};

export type TemplateBuilderReadData = {
  templateId: string;
  name: string;
  bgColour: string;
  selectedElementId: string | null;
  /** Every selected element id; `selectedElementId` is null when this holds more than one. */
  selectedElementIds: string[];
  elementCount: number;
  dataSources: TemplateBuilderReadDataSource[];
  elements: TemplateBuilderReadElement[];
  workingRevision: number;
};

type EditorElementLike = {
  id?: unknown;
  type?: unknown;
  name?: unknown;
  position?: { x?: unknown; y?: unknown; z?: unknown };
  size?: { width?: unknown; height?: unknown };
  rotation?: unknown;
  hidden?: unknown;
  locked?: unknown;
  sources?: unknown;
  data?: unknown;
};

type EditorStateLike = {
  name?: unknown;
  bgColour?: unknown;
  selectedElementId?: unknown;
  selectedElementIds?: unknown;
  elements?: unknown;
  dataSources?: unknown;
  workingRevision?: unknown;
};

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asFiniteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function looksLikeUrl(value: string): boolean {
  return /^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:');
}

function projectSources(value: unknown): TemplateBuilderReadSource[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const source = entry as { sourceId?: unknown; name?: unknown };
    if (typeof source.sourceId !== 'string' || typeof source.name !== 'string') {
      return [];
    }
    return [{ sourceId: source.sourceId, name: source.name }];
  });
}

function projectNameConfig(rawName: string): TemplateBuilderReadNameConfig | null {
  const parsed = parseElementName(rawName);
  if (!parsed.config) {
    return null;
  }

  const customAsset = parsed.config.customAssetConfig
    ? {
        folder: parsed.config.customAssetConfig.folder,
        fileTemplate: parsed.config.customAssetConfig.fileTemplate,
      }
    : null;

  return {
    isEditable: parsed.config.isEditable,
    customAsset,
  };
}

function extractContentTokens(
  content: string | null,
  sources: TemplateBuilderReadSource[],
): string[] {
  const tokens = new Set<string>();
  for (const source of sources) {
    tokens.add(source.name);
  }
  if (content) {
    const tokenPattern = new RegExp(CONTENT_TOKEN_PATTERN_SOURCE, 'gi');
    for (const match of content.matchAll(tokenPattern)) {
      if (match[1]) {
        tokens.add(match[1]);
      }
    }
  }
  return [...tokens];
}

function projectContentTemplate(type: string, content: unknown): string | null {
  if (typeof content !== 'string' || content.length === 0) {
    return null;
  }
  if (looksLikeUrl(content)) {
    if (URL_TEMPLATE_ELEMENT_TYPES.has(type) && hasDynamicContentTokens(content)) {
      return content;
    }
    return null;
  }
  if (type === 'TEXT') {
    return content;
  }
  if (content.includes('{') && content.includes('}')) {
    return content;
  }
  return null;
}

function projectDynamicFont(data: unknown): TemplateBuilderReadDynamicFont | null {
  if (!data || typeof data !== 'object' || !('dynamicFont' in data)) {
    return null;
  }
  const dynamicFont = (data as { dynamicFont?: unknown }).dynamicFont;
  if (!dynamicFont || typeof dynamicFont !== 'object') {
    return null;
  }

  const enabled = (dynamicFont as { enabled?: unknown }).enabled;
  if (enabled === false) {
    return { enabled: false };
  }
  if (enabled !== true) {
    return null;
  }

  const font = dynamicFont as {
    maxFontSize?: unknown;
    minFontSize?: unknown;
    lineHeight?: unknown;
    multiLine?: unknown;
  };

  return {
    enabled: true,
    maxFontSize: asNumber(font.maxFontSize),
    minFontSize: asNumber(font.minFontSize),
    lineHeight: asNumber(font.lineHeight),
    multiLine: font.multiLine === true,
  };
}

function asStyleString(value: unknown): string | null {
  const text = asStringOrNull(value);
  if (!text || looksLikeUrl(text)) {
    return null;
  }
  return text;
}

function projectColor(value: unknown): TemplateBuilderReadColor | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const color = value as { r?: unknown; g?: unknown; b?: unknown; a?: unknown };
  const r = asFiniteNumberOrNull(color.r);
  const g = asFiniteNumberOrNull(color.g);
  const b = asFiniteNumberOrNull(color.b);
  const a = asFiniteNumberOrNull(color.a);
  if (r == null || g == null || b == null || a == null) {
    return null;
  }
  return { r, g, b, a };
}

function projectFills(value: unknown): TemplateBuilderReadFill[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const fill = entry as {
      type?: unknown;
      color?: unknown;
      opacity?: unknown;
      gradientStops?: unknown;
      gradientHandlePositions?: unknown;
    };
    if (typeof fill.type !== 'string' || fill.type.length === 0) {
      return [];
    }

    const gradientStops = Array.isArray(fill.gradientStops)
      ? fill.gradientStops.flatMap((stop) => {
          if (!stop || typeof stop !== 'object') {
            return [];
          }
          const record = stop as { position?: unknown; color?: unknown };
          const position = asFiniteNumberOrNull(record.position);
          const color = projectColor(record.color);
          if (position == null || !color) {
            return [];
          }
          return [{ position, color }];
        })
      : null;

    const gradientHandlePositions = Array.isArray(fill.gradientHandlePositions)
      ? fill.gradientHandlePositions.flatMap((handle) => {
          if (!handle || typeof handle !== 'object') {
            return [];
          }
          const record = handle as { x?: unknown; y?: unknown };
          const x = asFiniteNumberOrNull(record.x);
          const y = asFiniteNumberOrNull(record.y);
          if (x == null || y == null) {
            return [];
          }
          return [{ x, y }];
        })
      : null;

    return [
      {
        type: fill.type,
        color: projectColor(fill.color),
        opacity: asFiniteNumberOrNull(fill.opacity),
        gradientStops,
        gradientHandlePositions,
      },
    ];
  });
}

function enumOrNull(value: unknown, allowed: Set<string>): string | null {
  return typeof value === 'string' && allowed.has(value) ? value : null;
}

function projectStyle(type: string, data: unknown): TemplateBuilderReadStyle | null {
  if (type !== 'TEXT' || !data || typeof data !== 'object' || !('style' in data)) {
    return null;
  }
  const style = (data as { style?: unknown }).style;
  if (!style || typeof style !== 'object') {
    return null;
  }

  const record = style as {
    textAlign?: unknown;
    textAlignVertical?: unknown;
    fontSize?: unknown;
    lineHeight?: unknown;
    letterSpacing?: unknown;
    fontFamily?: unknown;
    fontVariant?: unknown;
    fontWeight?: unknown;
    fontStyle?: unknown;
    fontStretch?: unknown;
    textCase?: unknown;
    color?: unknown;
    stroke?: unknown;
    strokeWeight?: unknown;
    fills?: unknown;
  };

  return {
    textAlign: enumOrNull(record.textAlign, TEXT_ALIGN),
    textAlignVertical: enumOrNull(record.textAlignVertical, TEXT_ALIGN_VERTICAL),
    fontSize: asFiniteNumberOrNull(record.fontSize),
    lineHeight: asFiniteNumberOrNull(record.lineHeight),
    letterSpacing: asFiniteNumberOrNull(record.letterSpacing),
    fontFamily: asStyleString(record.fontFamily),
    fontVariant: asStyleString(record.fontVariant),
    fontWeight: asFiniteNumberOrNull(record.fontWeight),
    fontStyle: asStyleString(record.fontStyle),
    fontStretch: asStyleString(record.fontStretch),
    textCase: enumOrNull(record.textCase, TEXT_CASE),
    color: asStyleString(record.color),
    stroke: asStyleString(record.stroke),
    strokeWeight: asFiniteNumberOrNull(record.strokeWeight),
    fills: projectFills(record.fills),
  };
}

function projectSize(value: EditorElementLike['size']): TemplateBuilderReadSize {
  return {
    width: asNumber(value?.width),
    height: asNumber(value?.height),
  };
}

function projectElement(element: EditorElementLike): TemplateBuilderReadElement | null {
  if (typeof element.id !== 'string' || typeof element.type !== 'string') {
    return null;
  }

  const name = typeof element.name === 'string' ? element.name : element.id;
  const sources = projectSources(element.sources);
  const data = element.data && typeof element.data === 'object' ? element.data : null;
  const rawContent =
    data && 'content' in data ? (data as { content?: unknown }).content : undefined;
  const contentTemplate = projectContentTemplate(element.type, rawContent);

  return {
    id: element.id,
    type: element.type,
    name,
    position: {
      x: asNumber(element.position?.x),
      y: asNumber(element.position?.y),
      z: asNumber(element.position?.z),
    },
    size: projectSize(element.size),
    rotation: asFiniteNumberOrNull(element.rotation),
    hidden: element.hidden === true,
    locked: element.locked === true,
    sources,
    nameConfig: projectNameConfig(name),
    contentTokens: extractContentTokens(
      typeof rawContent === 'string' ? rawContent : contentTemplate,
      sources,
    ),
    contentTemplate,
    dynamicFont: element.type === 'TEXT' ? projectDynamicFont(data) : null,
    style: projectStyle(element.type, data),
  };
}

function projectDataSources(value: unknown): TemplateBuilderReadDataSource[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const source = entry as { id?: unknown; type?: unknown };
    if (typeof source.id !== 'string' || typeof source.type !== 'string') {
      return [];
    }
    return [{ id: source.id, type: source.type }];
  });
}

export function sanitizeEditorSummary(
  state: EditorStateLike | null | undefined,
  templateId: string,
): TemplateBuilderReadData | null {
  if (!state || !Array.isArray(state.elements)) {
    return null;
  }

  const elements = (state.elements as EditorElementLike[])
    .map(projectElement)
    .filter((element): element is TemplateBuilderReadElement => element !== null);

  return {
    templateId,
    name: typeof state.name === 'string' ? state.name : '',
    bgColour: typeof state.bgColour === 'string' ? state.bgColour : '',
    selectedElementId: typeof state.selectedElementId === 'string' ? state.selectedElementId : null,
    selectedElementIds: Array.isArray(state.selectedElementIds)
      ? state.selectedElementIds.filter((id): id is string => typeof id === 'string')
      : typeof state.selectedElementId === 'string'
      ? [state.selectedElementId]
      : [],
    elementCount: elements.length,
    dataSources: projectDataSources(state.dataSources),
    elements,
    workingRevision:
      typeof state.workingRevision === 'number' && Number.isFinite(state.workingRevision)
        ? state.workingRevision
        : 0,
  };
}

export function sanitizeLogValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sanitizeLogValue);
  }

  if (typeof value === 'string' && looksLikeUrl(value)) {
    return '[redacted]';
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => {
        if (SENSITIVE_KEYS.has(key)) {
          return [key, '[redacted]'];
        }
        return [key, sanitizeLogValue(nested)];
      }),
    );
  }

  return value;
}
